import {
  currentStepIndexSelector,
  firstAvailableStepSelector,
  Step,
  useWizard,
  UseWizardOptions,
  WizardContext,
  WizardInterpreter,
  WizardState,
} from '@kizi/comparator-core';
import { TabView, TabViewProps } from '@kizi/shared-components';
import { useSelector } from '@xstate/react';
import React, { useEffect, useMemo } from 'react';

export interface TabViewWizardProps extends TabViewProps {
  steps: Step[];
  wizardOptions: Partial<UseWizardOptions>;
  previousState?: WizardState | null;
  wizardCallback?: (service: WizardInterpreter) => void;
  isScrollable?: boolean;
}

const defaultWizardOptions: UseWizardOptions = {
  startIndex: 0,
  execute: true,
  validators: {},
  transformers: {},
};

export const TabViewWizard: React.FC<TabViewWizardProps> = ({
  steps,
  wizardOptions,
  previousState = null,
  tabs,
  wizardCallback,
  tabProps,
  isScrollable = false,
  ...tabViewProps
}) => {
  const wizardService = useWizard(
    steps,
    { ...defaultWizardOptions, ...wizardOptions },
    previousState
  );
  const firstAvailableIndex = useSelector(
    wizardService,
    firstAvailableStepSelector
  );
  const currentStepIndex = useSelector(wizardService, currentStepIndexSelector);
  useEffect(() => {
    if (wizardCallback) wizardCallback(wizardService);
  }, [wizardService]);
  const _tabs: TabViewProps['tabs'] = useMemo(() => {
    return tabs.map((t, i) => ({
      label: t.label,
      disabled: i > firstAvailableIndex,
      content: t.content,
    }));
  }, [tabs, firstAvailableIndex]);
  const overriddenTabProps: TabViewProps['tabProps'] = (s, data) => {
    const ogTabProps =
      tabProps instanceof Function ? tabProps(s, data) : tabProps;
    return {
      ...ogTabProps,
      onClick: (e) => {
        if (ogTabProps && ogTabProps.onClick) ogTabProps.onClick(e);

        wizardService.send({ type: 'GOTO', index: data.index });
      },
    };
  };

  return (
    <WizardContext.Provider value={{ wizardService }}>
      <TabView
        {...tabViewProps}
        selectedTabIndex={currentStepIndex}
        tabs={_tabs}
        tabProps={overriddenTabProps}
        isScrollable={isScrollable}
      />
    </WizardContext.Provider>
  );
};
